import type { App } from '@keboola/api-client/developerPortal/types';

import { hasIcon } from './helpers';

/** A component is deployable once its repository has a URI. */
export const hasRepository = (app: Pick<App, 'repository'>): boolean =>
  Boolean(app.repository?.uri);

/**
 * Whether a component can be submitted for publishing.
 *
 * - All-or-nothing, so a half-finished component never reaches review.
 */
export const hasAllFieldsForPublishing = (app: App): boolean =>
  hasIcon(app) &&
  hasRepository(app) &&
  Boolean(app.name) &&
  Boolean(app.type) &&
  Boolean(app.shortDescription) &&
  Boolean(app.longDescription) &&
  Boolean(app.licenseUrl) &&
  Boolean(app.documentationUrl);

/**
 * Vendor list order: public, private, deprecated; alphabetical by name within each band.
 *
 * - Returns a new array — sorting in place would mutate the query cache.
 */
export const sortApps = (apps: readonly App[]): App[] =>
  [...apps].sort((a, b) => {
    if (a.isPublic !== b.isPublic) return a.isPublic === true ? -1 : 1;
    if (a.isDeprecated !== b.isDeprecated) return a.isDeprecated === true ? 1 : -1;
    return a.name.localeCompare(b.name);
  });

/** Only these three types can be opened in a project — the rest have no component page there. */
const PROJECT_LINKABLE_TYPES: readonly App['type'][] = ['extractor', 'writer', 'application'];

/**
 * A deep link to the component inside a Keboola project, for testing.
 *
 * - `projectUrl` is unverified free text: only `https://<host>.<stack domain>/admin/projects/<id>`
 *   passes, anything else returns `null`.
 * - Anchored with `^`, so a stack URL embedded in another host's URL is rejected.
 * - Segments are `[^/?#]+`: everything after the project id is stripped.
 */
const STACK_DOMAINS = [
  'keboola\\.com',
  'keboola\\.cloud',
  'keboola-testing\\.com',
  'keboola\\.dev',
];
const PROJECT_URL = new RegExp(
  `^https://[^/?#]+\\.(${STACK_DOMAINS.join('|')})/admin/projects/[^/?#]+`,
);

export const createKbcComponentUrl = (
  // `Vendor.projectUrl` is nullable on the wire, and absent when the vendor row is not loaded yet.
  projectUrl: string | null | undefined,
  appType: App['type'],
  appId: string,
): string | null => {
  if (projectUrl === undefined || projectUrl === null || projectUrl === '') return null;
  if (!PROJECT_LINKABLE_TYPES.includes(appType)) return null;

  const matched = PROJECT_URL.exec(projectUrl);
  if (matched === null) return null;

  return `${matched[0]}/components/${appId}`;
};
